import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { type TierDetails } from "~/api/types";
import { DocumentCard, DocumentLines } from "./DocumentCard";
import {
  type DocumentVersionSummaryChart,
  type CategoryMap,
  type DocumentVersionMap,
  type CategoryColumnMap,
} from "../helpers/chart";
import { Button } from "~/atoms/button";
import { Info } from "lucide-react";
import { CategoryModal } from "./CategoryModal";
import { DragDropProvider, type DragDropEventHandlers } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import { moveItemByIndex } from "~/helpers/moveItemByIndex";
import { updateDocumentOrder } from "~/api/v2/requests";

type OnDragEnd = NonNullable<DragDropEventHandlers["onDragEnd"]>;

interface CategoryBlockProps {
  id: number;
  title: string;
  tiers: TierDetails[];
  categoryMap: CategoryMap;
  documentVersionMap: DocumentVersionMap;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  setSelectedId: (id: number) => void;
  canManage: boolean;
  onReorder: () => void;
}

const CategoryBlock = ({
  id,
  title,
  tiers,
  categoryMap,
  documentVersionMap,
  documentRefsMap,
  containerRef,
  setSelectedId,
  canManage,
  onReorder,
}: CategoryBlockProps) => {
  const categoryData = categoryMap.get(id);

  return (
    <div className="min-w-[150px] border-l">
      <div className="sticky left-64 z-10 flex h-9 w-fit items-center px-2 text-sm">
        <div className="font-medium whitespace-nowrap">{title}</div>
        {canManage && (
          <CategoryModal
            categoryId={id}
            trigger={
              <Button variant="ghost" size="icon">
                <Info className="h-5 w-5 opacity-50" />
              </Button>
            }
          />
        )}
      </div>
      <div className="flex">
        {categoryData?.map((columnMap, i) => (
          <CategoryColumn
            key={i}
            columnIndex={i}
            tiers={tiers}
            documentVersionMap={documentVersionMap}
            documentRefsMap={documentRefsMap}
            containerRef={containerRef}
            categoryColumnMap={columnMap}
            setSelectedId={setSelectedId}
            canManage={canManage}
            onReorder={onReorder}
          />
        ))}
      </div>
      {!categoryData?.length &&
        tiers.map((tier, i) => (
          <div
            key={tier.id}
            className={cn("flex h-44 items-center justify-center gap-2 px-4", {
              "bg-gray-50": i % 2 !== 0,
            })}
          />
        ))}
    </div>
  );
};

interface CategoryColumnProps {
  columnIndex: number;
  tiers: TierDetails[];
  categoryColumnMap: CategoryColumnMap;
  documentVersionMap: Map<number, DocumentVersionSummaryChart>;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  setSelectedId: (id: number) => void;
  canManage: boolean;
  onReorder: () => void;
}

const CategoryColumn = ({
  columnIndex,
  tiers,
  categoryColumnMap,
  documentVersionMap,
  documentRefsMap,
  containerRef,
  setSelectedId,
  canManage,
  onReorder,
}: CategoryColumnProps) => {
  const [hoveredId, setHoveredId] = useState<number | undefined>();

  return (
    <div className="flex w-full flex-col">
      {tiers.map((tier, i) => {
        const documents = categoryColumnMap.get(tier.id) ?? [];
        return (
          <TierCell
            key={tier.id}
            tierId={tier.id}
            tierIndex={i}
            columnIndex={columnIndex}
            documents={documents}
            documentVersionMap={documentVersionMap}
            documentRefsMap={documentRefsMap}
            containerRef={containerRef}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            setSelectedId={setSelectedId}
            canManage={canManage}
            onReorder={onReorder}
          />
        );
      })}
    </div>
  );
};

interface TierCellProps {
  tierId: number;
  tierIndex: number;
  columnIndex: number;
  documents: DocumentVersionSummaryChart[];
  documentVersionMap: Map<number, DocumentVersionSummaryChart>;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  hoveredId?: number;
  setHoveredId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedId: (id: number) => void;
  canManage: boolean;
  onReorder: () => void;
}

const TierCell = ({
  tierId,
  tierIndex,
  columnIndex,
  documents,
  documentVersionMap,
  documentRefsMap,
  containerRef,
  hoveredId,
  setHoveredId,
  setSelectedId,
  canManage,
  onReorder,
}: TierCellProps) => {
  const { t } = useTranslation();
  const [localDocuments, setLocalDocuments] = useState(documents);
  const [reorderKey, setReorderKey] = useState(0);
  const localDocumentsRef = useRef(localDocuments);

  // Keep local state in sync when data changes from outside (e.g. SWR revalidation).
  useEffect(() => {
    setLocalDocuments(documents);
  }, [documents]);

  useEffect(() => {
    localDocumentsRef.current = localDocuments;
  }, [localDocuments]);

  const sortableType = `DOC_${tierId}_${columnIndex}`;

  const handleDragEnd = useCallback<OnDragEnd>(
    (event) => {
      const handle = async () => {
        if (event.canceled) return;
        if (!isSortableOperation(event.operation)) return;

        const source = event.operation.source;
        if (!source) return;

        const { initialIndex, index: targetIndex } = source;
        if (initialIndex === targetIndex) return;

        const reordered = moveItemByIndex(
          localDocumentsRef.current,
          initialIndex,
          targetIndex,
        );
        setLocalDocuments(reordered);
        setReorderKey((k) => k + 1);

        const orderPayload = reordered.map((doc, i) => ({
          id: doc.document.id,
          chartOrder: i,
        }));

        try {
          await updateDocumentOrder(orderPayload);
          onReorder();
        } catch {
          toast.error(t("toasts.error.reorderDocuments"));
          setLocalDocuments(documents);
        }
      };

      void handle();
    },
    [documents, onReorder, t],
  );

  const hasDraggableItems = canManage && localDocuments.length > 1;

  return (
    <div
      className={cn("flex h-44 items-center gap-2 px-4", {
        "bg-gray-50": tierIndex % 2 !== 0,
      })}
    >
      {hasDraggableItems ? (
        <>
          {localDocuments.map((document) => (
            <DocumentLines
              key={`lines-${document.id}-${reorderKey}`}
              documentVersion={document}
              documentVersionMap={documentVersionMap}
              documentRefsMap={documentRefsMap}
              containerRef={containerRef}
              hoveredId={hoveredId}
              reorderKey={reorderKey}
            />
          ))}
          <DragDropProvider onDragEnd={handleDragEnd}>
            {localDocuments.map((document, index) => (
              <SortableDocumentCard
                key={document.id}
                index={index}
                sortableType={sortableType}
                documentVersion={document}
                documentVersionMap={documentVersionMap}
                documentRefsMap={documentRefsMap}
                containerRef={containerRef}
                hoveredId={hoveredId}
                setHoveredId={setHoveredId}
                setSelectedId={setSelectedId}
              />
            ))}
          </DragDropProvider>
        </>
      ) : (
        localDocuments.map((document) => (
          <DocumentCard
            key={document.id}
            documentVersion={document}
            documentVersionMap={documentVersionMap}
            documentRefsMap={documentRefsMap}
            containerRef={containerRef}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            setSelectedId={setSelectedId}
          />
        ))
      )}
    </div>
  );
};

interface SortableDocumentCardProps {
  index: number;
  sortableType: string;
  documentVersion: DocumentVersionSummaryChart;
  documentVersionMap: Map<number, DocumentVersionSummaryChart>;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  hoveredId?: number;
  setHoveredId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedId: (id: number) => void;
}

const SortableDocumentCard = ({
  index,
  sortableType,
  documentVersion,
  documentVersionMap,
  documentRefsMap,
  containerRef,
  hoveredId,
  setHoveredId,
  setSelectedId,
}: SortableDocumentCardProps) => {
  const { ref, handleRef, isDragging } = useSortable({
    id: `${documentVersion.document.id}`,
    index,
    type: sortableType,
    accept: sortableType,
  });

  return (
    <div ref={ref}>
      <DocumentCard
        documentVersion={documentVersion}
        documentVersionMap={documentVersionMap}
        documentRefsMap={documentRefsMap}
        containerRef={containerRef}
        hoveredId={hoveredId}
        setHoveredId={setHoveredId}
        setSelectedId={setSelectedId}
        isDragging={isDragging}
        renderLines={false}
        handleRef={handleRef}
      />
    </div>
  );
};

export default CategoryBlock;
